import { CENTRAL_SCREEN_TOPOLOGY_MODE_VALUES } from "@automatry/schemas";
export const CONTROL_TOPOLOGY_CENTRAL_SCREEN_MODE_FAMILY_ID = "control-topology.central-screen-mode";
export const INTERFACE_DEMAND_POLICY_FLAG_FAMILY_IDS = [
    "interface-demand.hiu-interface",
    "interface-demand.ciu-interface",
    "interface-demand.electric-ufh-zone-interface",
    "interface-demand.towel-rail-interface",
    "interface-demand.wet-ufh-actuator-interface"
];
/**
 * F2 Lane-B wall (`_build/REVIEW-F2-FLAGS-DEMO.md` F2; contracts:
 * `_build/AGENTIC-SPEC-ARCHITECTURE.md`, `_build/F1-CONTROL-TOPOLOGY-CONTRACT.md`).
 *
 * ORCHESTRATOR RULING: `control-topology.central-screen-mode` is the F1-
 * sanctioned selector exception, and A4 adds the five `interface-demand.*`
 * families as a narrow takeoff-count scoring exception. Every OTHER F2 family
 * must reach the AI spec only through `serializeSpecPolicyFlagProviderBlock`;
 * it must never be visible to a verification-engine consumer such as
 * `resolveControlTopologySelector`.
 *
 * This allowlist is the explicit, discoverable choke point for that rule.
 * `resolveControlTopologySelector` already carries an internal type-guard
 * that filters by this same familyId (`isSpecPolicyControlTopologyFlagStub`),
 * so today this is defense-in-depth rather than a behavior change — but it
 * must be the thing every future verification consumer is required to call
 * before touching a policy flag set, not an accident of one guard's current
 * shape. Widening this allowlist requires an explicit orchestrator/Rich
 * carve-out, the same way the Wyvil exception itself was granted.
 */
export const ENGINE_INPUT_POLICY_FLAG_FAMILY_ALLOWLIST = [
    CONTROL_TOPOLOGY_CENTRAL_SCREEN_MODE_FAMILY_ID,
    ...INTERFACE_DEMAND_POLICY_FLAG_FAMILY_IDS
];
/**
 * Restricts a reviewed F2 flag set to exactly the F1-sanctioned selector
 * family before it may reach a verification-engine consumer. Every other
 * active family is dropped from the returned flag set and reported in
 * `excludedFamilyIds` so the caller can emit an honest trace note — the
 * excluded families are NOT lost, they still reach the AI spec via the
 * provider block, which is built from the UNFILTERED flag set elsewhere
 * (`buildGroundedSpecAgentContext` in `packages/agents/src/provider.ts`).
 */
export function restrictPolicyFlagSetToEngineInputAllowlist(flagSet) {
    if (!flagSet) {
        return { flagSet: undefined, excludedFamilyIds: [] };
    }
    const allowlist = new Set(ENGINE_INPUT_POLICY_FLAG_FAMILY_ALLOWLIST);
    const excludedFamilyIds = new Set();
    const activeFlags = flagSet.activeFlags.filter((flag) => {
        if (allowlist.has(flag.familyId)) {
            return true;
        }
        excludedFamilyIds.add(flag.familyId);
        return false;
    });
    return {
        flagSet: activeFlags.length > 0 ? { ...flagSet, activeFlags } : undefined,
        excludedFamilyIds: [...excludedFamilyIds]
    };
}
export function resolveControlTopologySelector(input) {
    const flag = input.policyFlagSet?.activeFlags.find((candidate) => isSpecPolicyControlTopologyFlagStub(candidate) &&
        flagAppliesToApartmentType(candidate, input));
    if (!flag) {
        return undefined;
    }
    return {
        mode: flag.value.mode,
        why: flag.why,
        confidence: flag.confidence.label,
        evidenceRefs: flag.evidenceRefs,
        policyFlagId: flag.id,
        provenance: flag.provenance ?? "ai-extracted",
        centralScreen: {
            productFamily: "Touch-7",
            location: "central screen zone"
        },
        zonePolicy: flag.value.mode === "central_screen_is_stat_for_own_zone"
            ? {
                ownZoneGetsSeparateStat: false,
                bedroomPolicy: "touch4_per_bedroom",
                bathroomPolicy: "slaved_to_bedroom_touch4",
                otherZonePolicy: "plain_remote_sensor"
            }
            : {
                ownZoneGetsSeparateStat: true,
                bedroomPolicy: "own_rule",
                bathroomPolicy: "own_stat",
                otherZonePolicy: "own_stat"
            },
        ...(flag.provenance === "user-override"
            ? {
                userOverride: {
                    overridden: true,
                    reason: flag.why
                }
            }
            : {})
    };
}
function isSpecPolicyControlTopologyFlagStub(candidate) {
    return (candidate.status !== "superseded" &&
        candidate.familyId === CONTROL_TOPOLOGY_CENTRAL_SCREEN_MODE_FAMILY_ID &&
        candidate.value.kind === "mode" &&
        isCentralScreenTopologyMode(candidate.value.mode) &&
        "why" in candidate &&
        "confidence" in candidate &&
        "evidenceRefs" in candidate);
}
export function bedroomTouch4CountForTopology(input) {
    const rows = input.takeoffRows.filter((row) => row.apartmentTypeId === input.apartmentTypeId);
    const rowsWithBeds = rows.filter((row) => row.beds !== undefined);
    if (rowsWithBeds.length > 0) {
        return rowsWithBeds.reduce((total, row) => total + (row.beds ?? 0), 0);
    }
    return Math.max(input.fallbackThermostatCount - 1, 0);
}
function isCentralScreenTopologyMode(mode) {
    return (typeof mode === "string" &&
        CENTRAL_SCREEN_TOPOLOGY_MODE_VALUES.includes(mode));
}
function flagAppliesToApartmentType(flag, input) {
    if (!flag.scope) {
        return true;
    }
    if (flag.scope.projectId && flag.scope.projectId !== input.projectId) {
        return false;
    }
    if (flag.scope.level === "apartment-type" &&
        flag.scope.apartmentTypeId &&
        flag.scope.apartmentTypeId !== input.apartmentTypeId) {
        return false;
    }
    return flag.scope.level === "project" || flag.scope.level === "apartment-type";
}
//# sourceMappingURL=controlTopologySelector.js.map